"use client";

import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { notificationsApi } from "@/lib/api/services/notifications.api";
import { useNotificationStore } from "@/store/notification-store";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCheck, Trash2 } from "lucide-react";

export default function NotificationsPage() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, removeNotification, setNotifications } = useNotificationStore();

  const { isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const data = await notificationsApi.list();
      const items = (data as { notifications?: Array<Record<string, unknown>> }).notifications ?? [];
      setNotifications(items as Array<{ id: string; title: string; message: string; type: "info" | "success" | "warning" | "error"; read: boolean; createdAt: string }>);
      return items;
    },
  });

  const handleMarkAsRead = async (id: string) => {
    markAsRead(id);
    try { await notificationsApi.markAsRead(id); } catch { /* silent */ }
  };

  const handleMarkAllAsRead = async () => {
    markAllAsRead();
    try { await notificationsApi.markAllAsRead(); } catch { /* silent */ }
  };

  const handleDelete = async (id: string) => {
    removeNotification(id);
    try { await notificationsApi.delete(id); } catch { /* silent */ }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Notifications</h1>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="h-5 px-2 text-[0.6875rem] font-medium tabular-nums">
            {unreadCount} unread
          </Badge>
          <Button variant="outline" size="sm" onClick={handleMarkAllAsRead}>
            <CheckCheck className="mr-2 h-4 w-4" /> Mark all read
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-4 p-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted/10 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-muted/10 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-16 text-center">
              <Bell className="mx-auto h-10 w-10 text-muted-foreground/30" />
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground">No notifications</h3>
              <p className="mt-1 text-sm text-muted-foreground">You are all caught up.</p>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-3 p-4 transition-colors hover:bg-muted/10 ${!notification.read ? "bg-primary/[0.03]" : ""}`}
                >
                  <div className={`mt-1 rounded-full p-1.5 shrink-0 ${!notification.read ? "bg-primary/15" : "bg-muted/10"}`}>
                    <Bell className={`h-4 w-4 ${!notification.read ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${!notification.read ? "text-foreground" : "text-muted-foreground"}`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{notification.message}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">{notification.createdAt}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!notification.read && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleMarkAsRead(notification.id)} title="Mark as read">
                        <CheckCheck className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(notification.id)} title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
